import { useState, useEffect, useRef } from 'react';
import { usePaymentApi } from '../paymentApiContext';
import logger from '../../../../utils/logging/logger';
import { formatErrorMessage } from './helpers';
import { fetchProductsList, fetchCoursesByCodesFromCatalog } from './courseProductFetchers';
import { calculateInitialBulkPricing } from './calculateInitialBulkPricing';

// The course/product-loading effect for usePaymentForm, extracted VERBATIM.
// Owns courses/loading/bulkPricing/bulkPricingLoading (nothing else in the
// hook family writes courses; bulkPricing is also written by the promo-code
// sub-hook via the setBulkPricing this hook returns). All other refs/state it
// touches are cross-cutting (also read/written by createPaymentIntent,
// handleSubmit, or the promo-code handlers) and are passed in by the caller.
export function useCourseProductFetch({
  courseId,
  courseIds,
  productIds,
  clientSecretRef,
  clientSecretFromParent,
  setError,
  coursesRef,
  bulkPricingRef,
  initialCoursePricesRef,
  abortControllerRef,
  isRestoringBulkPricingRef,
  prevBulkPricingRef,
  apiCallTimestampsRef,
  paymentIntentCreatedRef,
  RATE_LIMIT_DELAY,
}) {
  const paymentApi = usePaymentApi(); // signed-in api, or the guest adapter for new students
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkPricing, setBulkPricing] = useState(null);
  const [bulkPricingLoading, setBulkPricingLoading] = useState(false);
  // Read/written only within this effect (no other function in the hook
  // family touches them), so unlike the other refs threaded in via params,
  // they're created internally here.
  const isFetchingRef = useRef(false);
  const lastFetchedParamsRef = useRef(null);

  // Fetch course/product information
  // FIX: Use sessionStorage to track fetched params - persists across remounts
  // This prevents refetch when Elements remounts due to clientSecret changes
  useEffect(() => {
    // Create a unique key for the current params
    const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
    const storageKey = `paymentForm_fetched_${paramsKey}`;
    const coursesDataKey = `paymentForm_courses_${paramsKey}`;

    // CRITICAL: Check if we just fetched for these exact params (prevents immediate refetch)
    if (lastFetchedParamsRef.current === paramsKey && !isFetchingRef.current) {
      logger.debug('Just fetched for these params, skipping', { paramsKey });
      return;
    }

    // CRITICAL: Early return if already fetching - prevent concurrent fetches
    if (isFetchingRef.current) {
      logger.debug('Fetch already in progress, skipping', { paramsKey });
      return;
    }

    // FIX: Skip if data already fetched for these exact params (prevents refetch on remounts)
    // Check sessionStorage first - if it exists, we've already fetched for these params
    // We intentionally don't include courses.length in deps - we use sessionStorage to prevent loops
    // CRITICAL: On remount, courses will be empty, but sessionStorage persists
    // If we've already fetched, skip to prevent infinite loops
    const alreadyFetched = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === 'true';

    if (alreadyFetched) {
      // FIX: Always try to restore from storage first - this handles remounts gracefully
      const storedCoursesData = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(coursesDataKey);

      if (storedCoursesData) {
        // We have stored data - restore it
        try {
          const parsedCourses = JSON.parse(storedCoursesData);
          if (Array.isArray(parsedCourses) && parsedCourses.length > 0) {
            // Only restore if courses state is empty (prevents overwriting if already set)
            if (courses.length === 0) {
              setCourses(parsedCourses);
              // CRITICAL: Update ref immediately so payment intent creation can use it
              coursesRef.current = parsedCourses;
            }
            setLoading(false);

            // CRITICAL: Only restore bulk pricing if we DON'T already have a payment intent
            // If we have a payment intent, the bulk pricing should already be in state (React state persists across remounts)
            // Restoring it would trigger the discount change useEffect unnecessarily
            // clientSecret itself is owned by usePaymentIntent, which needs
            // courses/bulkPricing from THIS hook as input — a ref mirror
            // (kept in sync by an effect in the main hook) breaks the cycle
            // without changing behavior beyond the usual one-render lag
            // already tolerated by every other cross-hook ref in this family.
            const hasPaymentIntent = clientSecretFromParent || clientSecretRef.current || paymentIntentCreatedRef.current;

            // Define these variables outside the conditional blocks so they're accessible later
            const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
            const storedBulkPricing = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(bulkPricingKey);

            // Both branches restore identical parsed-bulk-pricing state; only
            // the gating condition and the debug-log label differ, so the
            // shared body is a local helper rather than copy-pasted twice.
            const restoreParsedBulkPricing = (label) => {
              try {
                const parsedBulkPricing = JSON.parse(storedBulkPricing);
                if (parsedBulkPricing && parsedBulkPricing.bulkPricing) {
                  // Set both state and ref to prevent payment intent recreation
                  // CRITICAL: Set flag and ref BEFORE state to prevent discount change detection
                  isRestoringBulkPricingRef.current = true;
                  prevBulkPricingRef.current = parsedBulkPricing;
                  setBulkPricing(parsedBulkPricing);
                  // Reset flag after state update completes
                  setTimeout(() => {
                    isRestoringBulkPricingRef.current = false;
                  }, 0);
                  logger.debug(label, {
                    discount: parsedBulkPricing.pricing?.discountAmount
                  });
                }
              } catch (e) {
                logger.debug('Failed to parse stored bulk pricing', { error: e });
                sessionStorage.removeItem(bulkPricingKey);
              }
            };

            if (!hasPaymentIntent) {
              // No payment intent yet - safe to restore bulk pricing
              if (storedBulkPricing && !bulkPricing) {
                restoreParsedBulkPricing('Bulk pricing restored from sessionStorage');
              }
            } else {
              // Payment intent exists - bulk pricing should be in state already
              // Only restore if state is empty (shouldn't happen, but safety check)
              // Reuse bulkPricingKey and storedBulkPricing from above
              if (!bulkPricing && storedBulkPricing) {
                restoreParsedBulkPricing('Bulk pricing restored from sessionStorage (payment intent exists)');
              }
            }

            // Only calculate bulk pricing if we don't have it in state or sessionStorage
            // Reuse hasPaymentIntent from above
            if (!hasPaymentIntent) {

              // CRITICAL: Only calculate bulk pricing if not already calculated and not in sessionStorage
              // This prevents recalculation on every remount
              // FIX: Only calculate bulk pricing for courses, not products
              if (parsedCourses.length > 1 && !bulkPricing && !storedBulkPricing && !bulkPricingLoading && !productIds) {
                const calculateBulkPricingForRestored = async () => {
                  try {
                    setBulkPricingLoading(true);
                    const courseIdsForBulk = parsedCourses.map(course => parseInt(course.id));
                    const now = Date.now();
                    const lastCallTime = apiCallTimestampsRef.current.calculateBulkPricing;
                    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
                      setBulkPricingLoading(false);
                      return;
                    }
                    apiCallTimestampsRef.current.calculateBulkPricing = now;

                    logger.debug('Calculating bulk pricing for restored courses', { courseIdsForBulk });
                    const bulkResponse = await paymentApi.calculateBulkPricing(courseIdsForBulk);
                    if (bulkResponse.success && bulkResponse.bulkPricing) {
                      // CRITICAL: Not restoring - this is a fresh calculation, so update prevRef
                      prevBulkPricingRef.current = bulkResponse;
                      setBulkPricing(bulkResponse);
                      // Store bulk pricing in sessionStorage for future remounts
                      if (typeof sessionStorage !== 'undefined') {
                        sessionStorage.setItem(bulkPricingKey, JSON.stringify(bulkResponse));
                      }
                      logger.info('Bulk pricing applied for restored courses', {
                        discount: bulkResponse.pricing.discountAmount,
                        finalPrice: bulkResponse.pricing.finalPrice
                      });
                    } else {
                      setBulkPricing(null);
                    }
                  } catch (error) {
                    logger.error('Error calculating bulk pricing for restored courses', { error });
                    setBulkPricing(null);
                  } finally {
                    setBulkPricingLoading(false);
                  }
                };
                calculateBulkPricingForRestored();
              }
            } else {
              // Payment intent already exists - don't restore bulk pricing to avoid triggering useEffects
              logger.debug('Skipping bulk pricing restoration - payment intent already exists', {
                hasClientSecret: !!clientSecretRef.current,
                hasClientSecretFromParent: !!clientSecretFromParent,
                paymentIntentCreated: paymentIntentCreatedRef.current
              });
            }

            // CRITICAL: Return early to prevent fetch - we already have the data
            return;
          } else {
            // Invalid parsed data
            sessionStorage.removeItem(coursesDataKey);
          }
        } catch (e) {
          // Invalid stored data - clear it but don't refetch (might be in progress)
          sessionStorage.removeItem(coursesDataKey);
        }
      }

      // If we have courses in state, we're good - don't refetch
      if (courses.length > 0) {
        setLoading(false);
        return;
      }

      // If we get here, we have the flag but no data - this means a previous fetch failed
      // Clear the flag and error state, then refetch
      const storedCoursesDataCheck = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(coursesDataKey);
      logger.debug('SessionStorage flag exists but no courses data found - clearing and refetching', {
        paramsKey,
        hasStoredData: !!storedCoursesDataCheck,
        coursesLength: courses.length
      });
      // Clear the flag and error so we can refetch
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(coursesDataKey);
      }
      setError(null); // Clear any previous error
      // Continue to fetch below (don't return)
    }

    // FIX #13: Create AbortController for this effect to cancel API calls on unmount
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchData = async () => {
      // CRITICAL: Set fetching flag to prevent concurrent fetches
      if (isFetchingRef.current) {
        logger.debug('Fetch already in progress, skipping duplicate call', { paramsKey });
        return;
      }
      isFetchingRef.current = true;
      setLoading(true);

      try {
        if (productIds) {
          const identifiers = productIds.split(',');

          let productsData;
          try {
            productsData = await fetchProductsList(identifiers, paymentApi);
          } catch (productError) {
            logger.error('Product fetch error', { productError, productIds, errorMessage: productError.message });
            // Provide more specific error message based on the error
            let errorMessage = 'Failed to load product information. Please try again.';
            if (productError.message) {
              errorMessage = productError.message;
            } else if (productError.status === 404) {
              errorMessage = 'Product not found. The product may have been removed or is no longer available.';
            } else if (productError.status === 401 || productError.status === 403) {
              errorMessage = 'Authentication required. Please log in and try again.';
            }
            setError(errorMessage);
            setLoading(false);
            // FIX: Clear any sessionStorage data for products when fetch fails
            if (typeof sessionStorage !== 'undefined') {
              const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
              const storageKey = `paymentForm_fetched_${paramsKey}`;
              const coursesDataKey = `paymentForm_courses_${paramsKey}`;
              sessionStorage.removeItem(storageKey);
              sessionStorage.removeItem(coursesDataKey);
            }
            return;
          }

          // FIX #8: Validate that all products are active/available
          const inactiveProducts = productsData.filter(product => !product.is_active);
          if (inactiveProducts.length > 0) {
            const inactiveNames = inactiveProducts.map(p => p.name).join(', ');
            const errorMessage = `The following products are no longer available: ${inactiveNames}`;
            setError(errorMessage);
            setLoading(false);
            // FIX: Clear sessionStorage when products are inactive
            if (typeof sessionStorage !== 'undefined') {
              const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
              const storageKey = `paymentForm_fetched_${paramsKey}`;
              const coursesDataKey = `paymentForm_courses_${paramsKey}`;
              sessionStorage.removeItem(storageKey);
              sessionStorage.removeItem(coursesDataKey);
            }
            return;
          }

          const courseLikeData = productsData.map(product => {
            return {
              id: product.id,
              name: product.name,
              code: `PROD-${product.id}`,
              description: product.description,
              price: parseFloat(product.price) || 0,
              currency: product.currency || 'USD',
              isPaidCourse: (parseFloat(product.price) || 0) > 0,
              type: 'product'
            };
          });

          setCourses(courseLikeData);
          if (typeof sessionStorage !== 'undefined' && courseLikeData && courseLikeData.length > 0) {
            sessionStorage.setItem(storageKey, 'true');
            // Also store the actual products data to restore on remount
            const coursesDataKey = `paymentForm_courses_${paramsKey}`;
            sessionStorage.setItem(coursesDataKey, JSON.stringify(courseLikeData));
            // CRITICAL: Mark these params as just fetched to prevent immediate refetch
            lastFetchedParamsRef.current = paramsKey;
            logger.debug('✅ Products set and sessionStorage updated', {
              productsCount: courseLikeData.length,
              productIds: courseLikeData.map(p => p.id),
              storageKey
            });
          }
          initialCoursePricesRef.current.clear();
          courseLikeData.forEach(product => {
            initialCoursePricesRef.current.set(product.id, parseFloat(product.price) || 0);
          });
          // FIX: Products don't use bulk pricing, so clear it and set loading to false
          setBulkPricing(null);
          setBulkPricingLoading(false);
        } else if (courseIds) {
          const identifiers = courseIds.split(',');
          const isNumeric = identifiers.every(id => /^\d+$/.test(id));

          let coursesData;

          if (isNumeric) {
            const coursePromises = identifiers.map(id => paymentApi.getCoursePricing(id));
            const responses = await Promise.all(coursePromises);
            coursesData = responses.map(response => response.course);
          } else {
            // Handle course codes
            coursesData = await fetchCoursesByCodesFromCatalog(identifiers, paymentApi);
          }

          // Validate that we have course data
          if (!coursesData || coursesData.length === 0) {
            throw new Error('No courses found with the provided codes');
          }

          // Open Enrollment allows payment/enrollment even when a course is inactive or past its end date.
          const inactiveCourses = coursesData.filter(course =>
            course.is_active === false && !course.openEnrollment
          );
          if (inactiveCourses.length > 0) {
            const inactiveNames = inactiveCourses.map(c => c.name || c.code).join(', ');
            throw new Error(`The following courses are no longer available: ${inactiveNames}`);
          }

          // Set courses state first
          setCourses(coursesData);
          // FIX: Only set sessionStorage if we actually have courses data
          // React state updates are async, so we check coursesData length, not courses state
          if (typeof sessionStorage !== 'undefined' && coursesData && coursesData.length > 0) {
            sessionStorage.setItem(storageKey, 'true'); // Mark as fetched for these params (persists across remounts)
            // Also store the actual courses data to restore on remount
            const coursesDataKey = `paymentForm_courses_${paramsKey}`;
            sessionStorage.setItem(coursesDataKey, JSON.stringify(coursesData));
            // CRITICAL: Mark these params as just fetched to prevent immediate refetch
            lastFetchedParamsRef.current = paramsKey;
          }

          // FIX #8: Store initial course prices for validation
          initialCoursePricesRef.current.clear();
          coursesData.forEach(course => {
            initialCoursePricesRef.current.set(course.id, parseFloat(course.price) || 0);
          });

          await calculateInitialBulkPricing({
            coursesData,
            productIds,
            paramsKey,
            apiCallTimestampsRef,
            RATE_LIMIT_DELAY,
            abortControllerRef,
            prevBulkPricingRef,
            setBulkPricing,
            setBulkPricingLoading,
            paymentApi,
          });
        } else if (courseId) {
          // Single course
          logger.debug('Single course', { courseId });
          const response = await paymentApi.getCoursePricing(courseId);
          logger.debug('Single course response', { response });
          setCourses([response.course]);
          // FIX: Only set sessionStorage if we actually have course data
          if (typeof sessionStorage !== 'undefined' && response.course) {
            sessionStorage.setItem(storageKey, 'true'); // Mark as fetched for these params (persists across remounts)
          }

          // FIX #8: Store initial course price for validation
          initialCoursePricesRef.current.clear();
          initialCoursePricesRef.current.set(response.course.id, parseFloat(response.course.price) || 0);
        }
      } catch (error) {
        logger.error('Error fetching course(s)', { error, courseIds, courseId, productIds, errorMessage: error.message, errorStack: error.stack });
        // FIX #11: Use formatErrorMessage for consistent error message formatting
        // FIX #20: Distinguish between network errors, timeout errors, and server errors
        // FIX: Prevent errors from bubbling up to ErrorBoundary by ensuring all errors are caught
        const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
        const isNetworkError = !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
        const isAbortError = error.name === 'AbortError' || abortControllerRef.current?.signal.aborted;

        // Don't set error state if request was aborted (component unmounted)
        if (isAbortError) {
          logger.debug('Request aborted, not setting error state');
          return;
        }

        // FIX: Provide product-specific error messages
        const defaultErrorMessage = productIds
          ? 'Failed to load product information. Please try again.'
          : 'Failed to load course information. Please try again.';

        const errorMessage = isTimeoutError
          ? 'Request timed out. Please check your internet connection and try again.'
          : isNetworkError
          ? 'Network error. Please check your connection and try again.'
          : (error.message && (error.message.includes('not found') || error.message.includes('not available'))
            ? error.message
            : defaultErrorMessage);

        // FIX: Ensure error state is set safely to prevent React errors
        try {
          setError(formatErrorMessage(errorMessage));
          setCourses([]); // Ensure courses array is empty so error message displays
          sessionStorage.removeItem(storageKey); // Remove on error so it can retry
        } catch (stateError) {
          // If setting state fails, log but don't throw (prevents ErrorBoundary trigger)
          logger.error('Failed to set error state', { stateError, originalError: error });
        }
      } finally {
        // FIX: Ensure loading state is always cleared, even if there's an error
        try {
          if (!abortControllerRef.current?.signal.aborted) {
            setLoading(false);
          }
          // CRITICAL: Always clear fetching flag
          isFetchingRef.current = false;
        } catch (stateError) {
          logger.error('Failed to clear loading state', { stateError });
          isFetchingRef.current = false;
        }
      }
    };

    // FIX: Only fetch if we have params and haven't already fetched
    // The alreadyFetched check above should prevent this, but add extra guard
    if (courseId || courseIds || productIds) {
      // Double-check sessionStorage before fetching (defense in depth)
      const doubleCheckFetched = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === 'true';

      if (!doubleCheckFetched && !isFetchingRef.current) {
        fetchData();
      } else {
        // Already fetched or currently fetching - ensure loading is false
        setLoading(false);
      }
    }

    // FIX #13: Cleanup - abort API calls if component unmounts
    return () => {
      abortController.abort();
      abortControllerRef.current = null;
      // Don't clear sessionStorage on cleanup - we want to prevent refetch on remount
      // The sessionStorage will persist across remounts (e.g., when Elements remounts due to clientSecret change)
    };
    // Intentionally excluding courses.length and error from dependencies:
    // - courses.length is only checked to clear sessionStorage if empty (one-time check)
    // - error is only checked for logging purposes
    // - Adding them would cause infinite loops since the effect sets courses/error
    // - We use sessionStorage to prevent refetch loops instead
    // CRITICAL: Also exclude bulkPricingLoading to prevent loops when bulk pricing is calculated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, courseIds, productIds]); // Only depend on URL params, not courses state or bulk pricing

  // Keep refs in sync with state
  // Deliberately declared HERE (immediately after the fetch effect, in the
  // same registration order as the original) rather than in the main hook:
  // the fetch effect's sessionStorage-restore path assigns coursesRef.current
  // directly and synchronously; if this sync effect were registered in a
  // DIFFERENT hook call before the fetch effect's, React would run it first
  // with this render's still-stale `courses` closure value, and it would
  // clobber the fetch effect's fresher direct assignment.
  useEffect(() => {
    coursesRef.current = courses;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  useEffect(() => {
    bulkPricingRef.current = bulkPricing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkPricing]);

  return { courses, setCourses, loading, setLoading, bulkPricing, setBulkPricing, bulkPricingLoading, setBulkPricingLoading };
}
